import React, { useState, useEffect } from 'react';
import { getChartData } from '../../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

function TestMACDChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('[TestMACDChart] Loading AAPL data with MACD...');
        const response = await getChartData('AAPL', '1y', 'macd');
        console.log('[TestMACDChart] Response:', response);

        const chartData = response?.data?.data || [];
        console.log(`[TestMACDChart] Got ${chartData.length} data points`);

        // Log first and last MACD values
        if (chartData.length > 0) {
          console.log('[TestMACDChart] First MACD:', {
            date: chartData[0].date,
            macd: chartData[0].macd,
            signal: chartData[0].macd_signal,
            histogram: chartData[0].macd_histogram
          });
          console.log('[TestMACDChart] Last MACD:', {
            date: chartData[chartData.length - 1].date,
            macd: chartData[chartData.length - 1].macd,
            signal: chartData[chartData.length - 1].macd_signal,
            histogram: chartData[chartData.length - 1].macd_histogram
          });
        }

        setData(chartData);
        setError(null);
      } catch (err) {
        console.error('[TestMACDChart] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return <div className="p-4">Loading MACD test chart...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  const hasData = data && data.length > 0;
  const hasMACDData = hasData && data.some(d => d.macd !== null && d.macd !== undefined);

  return (
    <div className="p-4 bg-gray-950">
      <h2 className="text-white mb-4">MACD Test Chart - AAPL 1 Year</h2>

      <div className="bg-gray-900 p-4 rounded mb-4">
        <p className="text-gray-300">Data points: {data.length}</p>
        <p className="text-gray-300">Has MACD data: {hasMACDData ? 'Yes' : 'No'}</p>
        {hasMACDData && (
          <>
            <p className="text-gray-300">
              Sample MACD values: {data.slice(-5).map(d => d.macd?.toFixed(2)).join(', ')}
            </p>
            <p className="text-gray-300">
              Sample Signal values: {data.slice(-5).map(d => d.macd_signal?.toFixed(2)).join(', ')}
            </p>
          </>
        )}
      </div>

      {hasMACDData ? (
        <div style={{ height: '400px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 20 }}>
              <CartesianGrid strokeDasharray="1 3" stroke="#374151" vertical={false} />

              <XAxis
                dataKey="date"
                stroke="#6b7280"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
              />

              <YAxis
                stroke="#6b7280"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                domain={['auto', 'auto']}
              />

              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                labelStyle={{ color: '#9ca3af' }}
              />

              {/* Zero line */}
              <ReferenceLine y={0} stroke="#6b7280" strokeWidth={1} />

              {/* MACD Line */}
              <Line
                type="monotone"
                dataKey="macd"
                stroke="#06b6d4"
                strokeWidth={2}
                dot={false}
                connectNulls={true}
                name="MACD"
              />

              {/* Signal Line */}
              <Line
                type="monotone"
                dataKey="macd_signal"
                stroke="#fb923c"
                strokeWidth={2}
                dot={false}
                connectNulls={true}
                name="Signal"
              />

              {/* Histogram */}
              <Line
                type="monotone"
                dataKey="macd_histogram"
                stroke="#9ca3af"
                strokeWidth={1}
                dot={false}
                connectNulls={true}
                name="Histogram"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="bg-gray-900 p-8 rounded text-center">
          <p className="text-gray-400">No MACD data available</p>
        </div>
      )}
    </div>
  );
}

export default TestMACDChart;